import { constantsJson, functionsJson, operatorsJson, operatorSymbolsJson } from "@corca/data";

import { FUNCTION_SYMPY_TO_KEY_MAP, OPERATOR_SYMPY_TO_KEY_MAP } from "../mappings";
import {
  constantNode,
  functionNode,
  groupNode,
  inlineNode,
  matrixNode,
  operatorNode,
  operatorSymbolNode,
  plainTextNode,
} from "../utils";

import { SympyParseError } from "./shared";

import type { SympyResult } from "../types";
import type { SympyParser } from "./SympyParser";
import type { NodeJson, OperatorTypeValue } from "@corca/data";

const eulerJson = constantsJson.find((c) => c.symbolProps?.key === "constant-eulers-number")!;
const commaSymbolJson = operatorSymbolsJson.find((op) => op.key === "symbol-comma")!;

export function parseSympyFunction(
  parser: SympyParser,
  sympy: Extract<SympyResult, { type: "function" }>,
  _opts?: { signed?: boolean; parent?: SympyResult },
): NodeJson[] {
  // `sympy.name` is already a fully-qualified name (e.g. "sp.sin", "sympy_json.statistics.Median")
  // matching the map keys in both directions — one lookup for stock
  // and custom functions alike.
  const functionName: string | undefined = FUNCTION_SYMPY_TO_KEY_MAP[sympy.name];
  const functionData = functionsJson.find((f) => f.key === functionName);
  let result: NodeJson[] = [];

  if (sympy.name === "sp.Piecewise") {
    const variantCount = sympy.args.length;
    const cells: NodeJson[] = [];
    sympy.args.forEach((arg, pairIndex) => {
      if (Array.isArray(arg) && arg.length === 2) {
        const tuple = arg as SympyResult[];
        const exprArg = tuple[0];
        const condArg = tuple[1];
        cells.push(inlineNode(...parser.parseResult(exprArg)));
        const isLastPair = pairIndex === sympy.args.length - 1;
        const isBooleanTrue =
          !Array.isArray(condArg) && condArg.type === "operator" && condArg.name === "BooleanTrue";
        if (isLastPair && isBooleanTrue) {
          cells.push(inlineNode(plainTextNode("otherwise.")));
        } else {
          cells.push(inlineNode(...parser.parseResult(condArg)));
        }
      } else {
        cells.push(...parser.parseResult(arg, { parent: sympy }));
      }
    });
    return [
      {
        type: "group",
        props: {
          casesSystemEnabled: true,
          openingSymbol: "{",
          closingSymbol: undefined,
        },
        children: [matrixNode(variantCount, 2, cells)],
      },
    ];
  }

  if (sympy.name === "sp.floor") {
    // Map to UI floor group
    return [
      groupNode("⌊", "⌋", [matrixNode(1, 1, [inlineNode(...parser.parseResult(sympy.args[0]))])]),
    ];
  }

  if (sympy.name === "sp.ceiling") {
    // Map to UI ceiling group
    return [
      groupNode("⌈", "⌉", [matrixNode(1, 1, [inlineNode(...parser.parseResult(sympy.args[0]))])]),
    ];
  }

  if (functionData) {
    const argsNode = inlineNode(
      ...sympy.args.flatMap((arg, idx) => [
        ...(idx > 0 ? [operatorSymbolNode(commaSymbolJson)] : []),
        ...parser.parseResult(arg),
      ]),
    );
    result = [functionNode(functionData, [], argsNode)];

    if (sympy.name === "sp.exp") {
      // TODO: choose between e^x and exp(x) based on context
      result = [constantNode(eulerJson), operatorNode("power", [argsNode])];
    }
  } else {
    const operatorName = OPERATOR_SYMPY_TO_KEY_MAP[sympy.name];
    const operatorData = Object.entries(operatorsJson).find(([_, o]) => o.key === operatorName);
    if (operatorData) {
      const argNodes = sympy.args.map((arg) => parser.parseResult(arg)).flat();
      result = [operatorNode(operatorData[0] as OperatorTypeValue, [inlineNode(...argNodes)])];
    } else {
      throw new SympyParseError(`Unknown function or operator: ${sympy.name}`);
    }
  }

  return result;
}
